import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as PendingIcon
} from '@mui/icons-material';
import { PageIndexingStatus } from '../types/timeline.types';

interface IndexingStatusWidgetProps {
  indexingStatus: PageIndexingStatus;
  engineName: string;
}

export const IndexingStatusWidget: React.FC<IndexingStatusWidgetProps> = ({
  indexingStatus,
  engineName
}) => {
  const getHealthColor = (health: string) => {
    switch (health) {
      case 'EXCELLENT': return 'success';
      case 'GOOD': return 'info';
      case 'NEEDS_ATTENTION': return 'warning';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return 'error';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'info';
      default: return 'default';
    }
  };

  const getActionTypeIcon = (actionType: string) => {
    switch (actionType) {
      case 'TECHNICAL_FIX': return <ErrorIcon fontSize="small" />;
      case 'CONTENT_IMPROVEMENT': return <TrendingUpIcon fontSize="small" />;
      case 'SUBMISSION': return <PendingIcon fontSize="small" />;
      default: return <WarningIcon fontSize="small" />;
    }
  };

  return (
    <Card elevation={1}>
      <CardContent>
        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
          Page Indexing Status - {engineName}
          <Chip 
            label={indexingStatus.indexingHealth} 
            color={getHealthColor(indexingStatus.indexingHealth) as any}
            size="small"
          />
        </Typography>

        {/* Indexing Metrics Grid */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {indexingStatus.indexedPages ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Indexed Pages
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {indexingStatus.pendingPages ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="error.main" fontWeight="bold">
                {indexingStatus.excludedPages ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Excluded
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {(indexingStatus.indexingRate ?? 0).toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Indexing Rate
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" fontWeight="medium">
              Indexing Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {indexingStatus.indexedPages ?? 0} of {indexingStatus.totalPages ?? 0} pages
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={indexingStatus.indexingRate ?? 0} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Recent Activity */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Recent Activity (7 days)</Typography>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="success.main">+{indexingStatus.recentlyIndexed ?? 0}</Typography>
                <Typography variant="caption">Indexed</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="info.main">+{indexingStatus.recentlySubmitted ?? 0}</Typography>
                <Typography variant="caption">Submitted</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="error.main">{indexingStatus.indexingErrors ?? 0}</Typography>
                <Typography variant="caption">Errors</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Top Issues */}
        {indexingStatus.topIndexingIssues && indexingStatus.topIndexingIssues.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Top Issues</Typography>
            <List dense>
              {indexingStatus.topIndexingIssues.slice(0, 3).map((issue, index) => (
                <ListItem key={index} sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <WarningIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={issue}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Recommendations */}
        {indexingStatus.recommendations && indexingStatus.recommendations.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Recommendations</Typography>
            <List dense>
              {indexingStatus.recommendations.slice(0, 3).map((rec, index) => (
                <ListItem key={index} sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {getActionTypeIcon(rec.actionType)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        {rec.title}
                        <Chip 
                          label={rec.priority} 
                          color={getPriorityColor(rec.priority) as any}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={`${rec.description} (Impact: ~${rec.estimatedImpact} pages)`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default IndexingStatusWidget;